import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon, Undo as RestoreIcon } from '@mui/icons-material';
import { relationshipOverridesApi } from '../../services/api';

function ProductCell({ product }) {
  if (!product) return <TableCell>—</TableCell>;
  return (
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {product.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {product.retailer_id}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {[product.brand, product.color].filter(Boolean).join(' · ')}
      </Typography>
      {product.product_url && (
        <Typography
          variant="caption"
          component="a"
          href={product.product_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'none' }}
        >
          View ↗
        </Typography>
      )}
    </TableCell>
  );
}

export default function OverridesTab() {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await relationshipOverridesApi.list();
      setOverrides(data.overrides || []);
    } catch (err) {
      setError(err.message || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      await relationshipOverridesApi.restore(id);
      setOverrides(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to restore override');
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} gap={1}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Relationship Overrides
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {overrides.length} active
        </Typography>
        <IconButton onClick={loadOverrides} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : overrides.length === 0 ? (
        <Alert severity="info">No active overrides. Disconnected product pairs will appear here.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product A</TableCell>
                <TableCell>Product B</TableCell>
                <TableCell>Signal tags</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((override) => (
                <TableRow key={override.id} hover>
                  <ProductCell product={override.product_a} />
                  <ProductCell product={override.product_b} />
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {(override.signal_tags || []).map(tag => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={override.ai_reason} placement="top">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          cursor: 'help',
                        }}
                      >
                        {override.ai_reason}
                        {override.admin_note && (
                          <em> — {override.admin_note}</em>
                        )}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {override.created_by}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {override.created_at
                        ? new Date(override.created_at).toLocaleDateString('nl-NL', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Restore — pair reappears after next ETL run">
                      <span>
                        <Button
                          size="small"
                          startIcon={
                            restoringId === override.id
                              ? <CircularProgress size={12} />
                              : <RestoreIcon />
                          }
                          disabled={restoringId === override.id}
                          onClick={() => handleRestore(override.id)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Restore
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
